/**
 * Component selection per D-02 `inclusive` semantics.
 *
 *   inclusive === true   — include predecessors AND successors transitively
 *                          (matches v1 `linksBuilder.getLinkedTasks(id, true)`).
 *   inclusive === false  — successor cone only (matches v1
 *                          `getLinkedTasks(id, false)` and the post-link-delete
 *                          reschedule trigger at
 *                          `gantt/sources/ext/auto_scheduling/ui_handlers.ts:46`).
 *
 * Operates over the SchedulingGraph's scheduling edges ONLY — containment is
 * not traversed here. Containment-driven scope (a deep leaf's change flowing UP
 * to its ancestor summaries and their cross-summary links, and a summary's
 * descendants coming along as a moving block) is layered on top in
 * `plan.ts` after this base component is computed (the ancestor-summary and
 * GS-984 descendant expansions). Keeping `selectComponent` purely
 * scheduling-edge-based lets `plan.ts` call it repeatedly from those expanded
 * roots without it pulling in containment paths twice.
 *
 * Implementation: pure BFS over `graph.successors` (+ `graph.predecessors`
 * when inclusive) from `rootTaskId`. O(V+E). Returns a Set of in-scope task
 * IDs; callers (the planner) skip any node not in the Set during the
 * scheduling pass.
 */

import type { NormalizedTaskID } from "../types";
import type { SchedulingGraph } from "../graph/types";

export interface ComponentScope {
  readonly nodes: ReadonlySet<NormalizedTaskID>;
}

export function selectComponent(
  graph: SchedulingGraph,
  rootTaskId: NormalizedTaskID,
  inclusive: boolean
): ComponentScope {
  const visited = new Set<NormalizedTaskID>();
  // If the root isn't in the graph, return an empty scope. Caller (planner)
  // treats empty scope as "nothing to plan."
  if (!graph.nodes.has(rootTaskId)) return { nodes: visited };

  const queue: NormalizedTaskID[] = [rootTaskId];
  visited.add(rootTaskId);
  while (queue.length > 0) {
    const id = queue.shift()!;

    const succs = graph.successors.get(id);
    if (succs) {
      for (const e of succs) {
        if (!visited.has(e.target) && graph.nodes.has(e.target)) {
          visited.add(e.target);
          queue.push(e.target);
        }
      }
    }

    if (inclusive) {
      const preds = graph.predecessors.get(id);
      if (preds) {
        for (const e of preds) {
          if (!visited.has(e.source) && graph.nodes.has(e.source)) {
            visited.add(e.source);
            queue.push(e.source);
          }
        }
      }
    }
  }
  return { nodes: visited };
}

/**
 * Strict predecessor-closure of `rootTaskId`: every node reachable by walking
 * `graph.predecessors` edges backward from the root. The root itself is NOT
 * included (the result is "the tasks upstream of the root").
 *
 * Used by the planner to identify the upstream boundary of a scoped reschedule.
 * v1's drag handler collects the dragged task's dependency relations but never
 * re-resolves the upstream (predecessor) tasks — they act as fixed anchors that
 * only bound the dragged task and its downstream. Under `gap_behavior:"compress"`
 * the v2 pass would otherwise pull those predecessors to their earliest feasible
 * start (they're in the inclusive scope), so the planner consults this set to
 * keep them at their preferred (anchor) date instead. See
 * `schedule_pass.ts` (`useAsRootAnchor`).
 */
export function selectPredecessorClosure(
  graph: SchedulingGraph,
  rootTaskId: NormalizedTaskID
): ReadonlySet<NormalizedTaskID> {
  const upstream = new Set<NormalizedTaskID>();
  if (!graph.nodes.has(rootTaskId)) return upstream;

  const seen = new Set<NormalizedTaskID>([rootTaskId]);
  const queue: NormalizedTaskID[] = [rootTaskId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    const preds = graph.predecessors.get(id);
    if (!preds) continue;
    for (const e of preds) {
      if (seen.has(e.source) || !graph.nodes.has(e.source)) continue;
      seen.add(e.source);
      upstream.add(e.source);
      queue.push(e.source);
    }
  }
  return upstream;
}
